/* THE STAGE'S STATE. One world for the whole console: which run it is showing, the case the agent is on and the bed in hand. The location holds what a link can name (the run and the bed), so the back button and a pasted link both work; everything else is here. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react'
import { useAsync, type Async } from '../../lib/hooks.tsx'
import { match, navigate, useLocation } from '../../lib/router.tsx'
import { v1 } from '../api.ts'
import { usePoll, useRuns } from '../context.tsx'
import { homeState, readKept, tabStore, writeKept, type HomeState } from '../model/home.ts'
import { BEDS, bedAtLocation, bedHref, bedOf, dotsOf, type Dotted } from '../model/stage.ts'
import { analysedRun } from '../model/ward.ts'
import type { LivingScene } from '../../world3d/living.ts'
import type { AnalysisBody, Run } from '../types.ts'

export type Stage = {
  run: Run | null
  body: AnalysisBody | null
  analysis: Async<AnalysisBody | null>
  home: HomeState
  count: number
  dots: Dotted[]
  /** Every how many cases `dots` takes one, and how many the run holds. */
  stride: number
  total: number
  /** The case the agent is on. */
  at: number
  setAt: (i: number) => void
  bed: number | null
  hover: number | null
  setHover: (bay: number | null) => void
  select: (bay: number | null) => void
  connect: Run | null
  setConnect: (r: Run | null) => void
  scene: MutableRefObject<LivingScene | null>
  liveBed: number | null
}

const Ctx = createContext<Stage | null>(null)
export const useStage = (): Stage => {
  const s = useContext(Ctx)
  if (!s) throw new Error('useStage outside the stage')
  return s
}

export function StageProvider({ children }: { children: ReactNode }) {
  const { path, query } = useLocation()
  const runs = useRuns()
  const list = useMemo(() => runs.data?.runs ?? [], [runs.data])
  const scene = useRef<LivingScene | null>(null)

  // The run a Connect door opened in this tab: read while it can still change, so the world wakes when the Resident arrives, on any page.
  const [connect, setConnect] = useState<Run | null>(null)
  useEffect(() => {
    const k = readKept(tabStore())
    if (k) void v1.run(k.id).then((r) => setConnect(r.run), () => writeKept(tabStore(), null))
  }, [])
  const connectId = connect?.id ?? null
  const changing = connect !== null && (connect.status === 'preparing' || connect.status === 'open' || connect.status === 'scoring')
  usePoll(changing, () => {
    if (connectId) void v1.run(connectId).then((r) => setConnect(r.run), () => undefined)
  }, 1000)
  const wasChanging = useRef(false)
  const reloadRuns = useRef(runs.reload)
  reloadRuns.current = runs.reload
  useEffect(() => {
    if (wasChanging.current && !changing) reloadRuns.current()
    wasChanging.current = changing
  }, [changing])

  const home = homeState(list, connect)
  usePoll(home.kind === 'preparing' || home.kind === 'scoring' || (home.kind === 'live' && connect === null), runs.reload, 2000)
  // Slowly otherwise, so a Resident connected from a terminal wakes the world without a reload.
  usePoll(home.kind === 'verdict', runs.reload, 8000)

  // The run on stage: the one the page names, the one the link names, the one a Resident is playing, else the newest scored.
  const named = match('/runs/:id', path)?.id ?? match('/runs/:id/axis/:axis', path)?.id ?? query.get('run')
  const run = useMemo(() => {
    if (named && named !== 'new') return list.find((r) => r.id === named) ?? null
    if (home.kind === 'live' || home.kind === 'verdict' || home.kind === 'scoring') return home.run
    return analysedRun(list)
  }, [named, list, home])
  const live = home.kind === 'live' && run?.id === home.run.id
  const analysis = useAsync((s) => (run && !live && (run.status === 'complete' || run.status === 'stopped') ? v1.analysis(run.id, s) : Promise.resolve(null)), [run?.id, run?.status, live])
  const body = analysis.data && run && analysis.data.run.id === run.id ? analysis.data : null
  const all = useMemo(() => dotsOf(body), [body])
  // However many cases the run holds, the agent works a sample of them on stage, evenly through the run.
  const stride = Math.max(1, Math.ceil(all.length / 48))
  const dots = useMemo(() => all.filter((_, i) => i % stride === 0), [all, stride])

  const raw = bedAtLocation(path, query)
  const bed = raw === null ? null : bedOf(raw)
  const [hover, setHover] = useState<number | null>(null)
  const [at, setAt] = useState(0)

  const select = useCallback((bay: number | null) => {
    if (bay === null) navigate(query.get('run') ? `/?run=${encodeURIComponent(query.get('run') as string)}` : '/')
    else navigate(bedHref(run?.id ?? null, bay))
  }, [query, run?.id])

  const liveBed = home.kind === 'live' && home.live.case >= 1 ? bedOf(home.live.case) : null
  const value: Stage = { run, body, analysis, home, count: BEDS, dots, stride, total: all.length, at, setAt, bed, hover, setHover, select, connect, setConnect, scene, liveBed }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
